import type * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import type { MeetingState } from './week-view.tsx';

export interface DayGroupProps extends Omit<React.ComponentProps<'section'>, 'style'> {
  /* Weekday name, or "Vandaag" for the current day. */
  day: string;
  /* The date, e.g. "15 juni". */
  date: string;
  today?: boolean;
}

export function DayGroup({
  day,
  date,
  today = false,
  className,
  children,
  ...props
}: DayGroupProps) {
  return (
    <section data-slot='day-group' className={cn('mb-6.5', className)} {...props}>
      <div className='mb-2.5 flex items-baseline gap-2.5 pl-0.5'>
        <h3
          className={cn(
            'text-sm font-bold tracking-wide uppercase',
            today ? 'text-success' : 'text-ink',
          )}
        >
          {day}
        </h3>
        <span className='text-sm text-ink-muted'>{date}</span>
      </div>
      <div className='overflow-hidden rounded-lg border border-line bg-surface'>
        {children}
      </div>
    </section>
  );
}

export type EmptyDayProps = Omit<React.ComponentProps<'p'>, 'style'>;

/*
 * Says the day holds no Wundo conversations — not that it holds no meetings.
 * The distinction matters: the calendar may well be full.
 */
export function EmptyDay({ className, ...props }: EmptyDayProps) {
  return (
    <p
      data-slot='empty-day'
      className={cn('px-5 py-5.5 text-center text-base text-ink-muted', className)}
      {...props}
    />
  );
}

export const meetingRowVariants = cva(
  [
    'group relative flex w-full items-center gap-4 border-t border-fill px-5.5 py-5 text-left transition',
    'first:border-t-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
  ],
  {
    variants: {
      state: {
        tracked: 'cursor-pointer hover:bg-fill-faint',
        candidate: 'cursor-pointer bg-fill-faint hover:bg-fill',
        inert: 'bg-fill-faint',
      },
    },
    defaultVariants: {
      state: 'tracked',
    },
  },
);

export type MeetingRowVariants = VariantProps<typeof meetingRowVariants>;

export interface MeetingRowProps extends Omit<
  React.ComponentProps<'button'>,
  'style' | 'title' | 'children'
> {
  title: string;
  /* Start time, e.g. "10:00". */
  time: string;
  /* Duration, e.g. "30 min". */
  duration: string;
  /* The kind pill, or the "+ Wundo" pill on a row that is not in Wundo yet. */
  badge?: React.ReactNode;
  /* A control of its own in the badge's place; see `MeetingCard`. */
  action?: React.ReactNode;
  /* The same three states the week card has; see `MeetingState`. */
  state?: MeetingState;
}

/*
 * A meeting in the list view.
 *
 * Rows start with the time and the name — no leading type icon. The type is
 * the pill's job, and an icon tile would say the same thing twice.
 *
 * Only a row that is already in Wundo carries a trailing mark: the chevron says
 * "open this". A row that is not says so in its body, with the "+ Wundo" pill,
 * and repeating that as a glyph at the far edge says the same thing twice.
 */
export function MeetingRow({
  title,
  time,
  duration,
  badge,
  action,
  state = 'tracked',
  className,
  ...props
}: MeetingRowProps) {
  const tracked = state === 'tracked';
  const pill = action ? (
    <span className='relative z-1 flex min-w-0'>{action}</span>
  ) : (
    badge
  );
  const body = (
    <>
      <span className='w-18 shrink-0 text-right'>
        <span
          className={cn(
            'block text-sm font-bold',
            tracked ? 'text-ink' : 'text-ink-muted',
          )}
        >
          {time}
        </span>
        <span className='mt-px block text-2xs font-semibold text-ink-muted'>
          {duration}
        </span>
      </span>
      <span className='min-w-0 flex-1'>
        <span
          className={cn(
            'block text-md leading-tight font-semibold',
            tracked ? 'text-ink' : 'text-ink-muted',
          )}
        >
          {title}
        </span>
        {/* The container a pill measures to know whether its full label fits; see `PillLabel`. */}
        {pill && <span className='mt-1.25 flex @container/pill'>{pill}</span>}
      </span>
      {tracked && (
        <span className='shrink-0 text-ink-muted'>
          <ChevronRight className='size-4' aria-hidden='true' />
        </span>
      )}
    </>
  );

  /* Same reasoning as the week card: nothing to do means not a button. */
  if (state === 'inert') {
    return (
      <div
        data-slot='meeting-row'
        data-state={state}
        className={cn(meetingRowVariants({ state }), className)}
      >
        {body}
      </div>
    );
  }

  /* Same reasoning as the week card: a control of its own sits on a stretched button. */
  if (action) {
    return (
      <div
        data-slot='meeting-row'
        data-state={state}
        className={cn(meetingRowVariants({ state }), className)}
      >
        <button
          data-slot='meeting-row-open'
          type='button'
          className='absolute inset-0 cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink-faint'
          {...props}
        />
        {body}
      </div>
    );
  }

  return (
    <button
      data-slot='meeting-row'
      data-state={state}
      type='button'
      className={cn(meetingRowVariants({ state }), className)}
      {...props}
    >
      {body}
    </button>
  );
}
